// shared/types/permissions.ts

/**
 * Roles del sistema ALPRO
 * - property_manager: Gestiona propiedades, unidades, inquilinos, finanzas
 * - board_member: Acceso de solo lectura al portal de gobierno
 * - owner: Ve sus propiedades y reportes financieros
 * - renter: Ve su unidad, paga rent, crea work orders
 * - super_admin: Operador de plataforma (EP-07 / SA-00). Hereda todos los
 *   permisos de property_manager + acceso cross-organización. Documentado
 *   en docs/adr/011-super-admin-role-model.md (ADR 011, D1+D3).
 */
export const ROLES = ['property_manager', 'board_member', 'owner', 'renter', 'super_admin'] as const;
export type Role = typeof ROLES[number];

/**
 * Recursos del sistema sobre los que se aplican permisos
 *
 * EP-07 / SA-00 (ADR 011 D3): se agregan recursos cross-tenant exclusivos del
 * Super Admin: `organizations` (lista cross-org), `global_dashboard` (SA-03),
 * `audit_log` (lectura del trail de withGlobalAccess).
 */
export const RESOURCES = [
  'properties',
  'units',
  'renters',
  'owners',
  'invoices',
  'payments',
  'transactions',
  'work_orders',
  'violations',
  'amenities',
  'documents',
  'reports',
  'users',
  'settings',
  'organization',
  'vendors',
  // EP-07 / Super Admin only (cross-tenant).
  'organizations',
  'global_dashboard',
  'audit_log',
  'announcements',
] as const;
export type Resource = typeof RESOURCES[number];

/**
 * Acciones posibles sobre recursos
 */
export const ACTIONS = ['create', 'read', 'update', 'delete', 'manage'] as const;
export type Action = typeof ACTIONS[number];

/**
 * Estructura de un permiso individual
 */
export interface Permission {
  resource: Resource;
  actions: Action[];
}

/**
 * Mapa de permisos por rol (configuración default)
 * El backend puede extender/modificar esto por organización
 */
export const DEFAULT_ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  property_manager: [
    { resource: 'properties', actions: ['create', 'read', 'update', 'delete'] },
    { resource: 'units', actions: ['create', 'read', 'update', 'delete'] },
    { resource: 'renters', actions: ['create', 'read', 'update', 'delete'] },
    { resource: 'owners', actions: ['create', 'read', 'update'] },
    { resource: 'invoices', actions: ['create', 'read', 'update', 'delete'] },
    { resource: 'payments', actions: ['create', 'read', 'update'] },
    { resource: 'transactions', actions: ['create', 'read', 'update'] },
    { resource: 'work_orders', actions: ['create', 'read', 'update', 'delete'] },
    { resource: 'violations', actions: ['create', 'read', 'update', 'delete'] },
    { resource: 'amenities', actions: ['create', 'read', 'update', 'delete'] },
    { resource: 'documents', actions: ['create', 'read', 'update', 'delete'] },
    { resource: 'reports', actions: ['read'] },
    { resource: 'users', actions: ['read'] },
    { resource: 'settings', actions: ['read', 'update'] },
    { resource: 'organization', actions: ['read', 'update'] },
    { resource: 'vendors', actions: ['create', 'read', 'update', 'delete'] },
    { resource: 'announcements', actions: ['create', 'read', 'update', 'delete'] },
  ],
  board_member: [
    { resource: 'properties', actions: ['read'] },
    { resource: 'units', actions: ['read'] },
    { resource: 'renters', actions: ['read'] },
    { resource: 'owners', actions: ['read'] },
    { resource: 'invoices', actions: ['read'] },
    { resource: 'payments', actions: ['read'] },
    { resource: 'transactions', actions: ['read'] },
    { resource: 'work_orders', actions: ['read'] },
    { resource: 'violations', actions: ['read'] },
    { resource: 'amenities', actions: ['read'] },
    { resource: 'documents', actions: ['read'] },
    { resource: 'reports', actions: ['read'] },
    { resource: 'users', actions: ['read'] },
    { resource: 'settings', actions: ['read'] },
    { resource: 'organization', actions: ['read'] },
    { resource: 'vendors', actions: ['read'] },
    { resource: 'announcements', actions: ['read'] },
  ],
  owner: [
    { resource: 'properties', actions: ['read'] },
    { resource: 'units', actions: ['read'] },
    { resource: 'invoices', actions: ['read'] },
    { resource: 'payments', actions: ['read'] },
    { resource: 'transactions', actions: ['read'] },
    { resource: 'work_orders', actions: ['read'] },
    { resource: 'documents', actions: ['read'] },
    { resource: 'reports', actions: ['read'] },
    { resource: 'announcements', actions: ['read'] },
  ],
  renter: [
    { resource: 'units', actions: ['read'] },
    { resource: 'invoices', actions: ['read'] },
    { resource: 'payments', actions: ['create', 'read'] },
    { resource: 'work_orders', actions: ['create', 'read'] },
    { resource: 'amenities', actions: ['read'] },
    { resource: 'documents', actions: ['read'] },
    { resource: 'announcements', actions: ['read'] },
  ],
  /**
   * EP-07 / SA-00 (ADR 011 D3): Super Admin = PM potenciado.
   * Hereda todos los permisos del property_manager, agrega acceso cross-org y
   * a los recursos globales (organizations, global_dashboard, audit_log).
   * Los recursos marcados con "(cross-org)" no se aplican a un org específico;
   * la implementación backend los gatea via `requireSuperAdmin` +
   * `withGlobalAccess()`. Los recursos `users: manage` permite ascender /
   * descender usuarios (ver sección 5 del doc de modelo).
   */
  super_admin: [
    { resource: 'properties', actions: ['create', 'read', 'update', 'delete'] }, // cross-org
    { resource: 'units', actions: ['create', 'read', 'update', 'delete'] }, // cross-org
    { resource: 'renters', actions: ['create', 'read', 'update', 'delete'] }, // cross-org
    { resource: 'owners', actions: ['create', 'read', 'update', 'delete'] }, // cross-org
    { resource: 'invoices', actions: ['create', 'read', 'update', 'delete'] }, // cross-org
    { resource: 'payments', actions: ['create', 'read', 'update', 'delete'] }, // cross-org
    { resource: 'transactions', actions: ['create', 'read', 'update', 'delete'] }, // cross-org
    { resource: 'work_orders', actions: ['create', 'read', 'update', 'delete'] }, // cross-org
    { resource: 'violations', actions: ['create', 'read', 'update', 'delete'] }, // cross-org
    { resource: 'amenities', actions: ['create', 'read', 'update', 'delete'] }, // cross-org
    { resource: 'documents', actions: ['create', 'read', 'update', 'delete'] }, // cross-org
    { resource: 'reports', actions: ['read'] }, // cross-org
    { resource: 'users', actions: ['read', 'create', 'update', 'delete', 'manage'] }, // cross-org
    { resource: 'settings', actions: ['create', 'read', 'update', 'delete'] }, // cross-org
    { resource: 'organization', actions: ['create', 'read', 'update', 'delete'] }, // cross-org
    // Recursos globales (EP-07 nuevos) — disponibles solo vía `withGlobalAccess()`.
    { resource: 'organizations', actions: ['create', 'read', 'update', 'delete', 'manage'] },
    { resource: 'global_dashboard', actions: ['read'] },
    { resource: 'audit_log', actions: ['read'] },
    // CRUD sobre anuncios es de property_manager; SA hereda el mismo set
    // para poder administrarlos desde el panel global (EP-07 / SA-00).
    { resource: 'announcements', actions: ['create', 'read', 'update', 'delete'] }, // cross-org
    // SA-14: vendors scope intra-org (org-wide + company-specific).
    { resource: 'vendors', actions: ['create', 'read', 'update', 'delete'] },
  ],
};

/**
 * Helper: Verifica si un rol tiene permiso sobre un recurso/acción
 */
export function hasPermission(
  permissions: Permission[],
  resource: Resource,
  action: Action
): boolean {
  const resourcePermission = permissions.find(p => p.resource === resource);
  if (!resourcePermission) {return false;}
  return resourcePermission.actions.includes(action) || resourcePermission.actions.includes('manage');
}

/**
 * Helper: Verifica si un rol puede realizar CUALQUIER acción sobre un recurso
 */
export function canAccessResource(permissions: Permission[], resource: Resource): boolean {
  return permissions.some(p => p.resource === resource && p.actions.length > 0);
}

/**
 * Type guard para validar si un string es un Role válido
 */
export function isValidRole(role: string): role is Role {
  return ROLES.includes(role as Role);
}

/**
 * Helper: Obtiene los permisos default para un rol
 */
export function getDefaultPermissions(role: Role): Permission[] {
  return DEFAULT_ROLE_PERMISSIONS[role] || [];
}

/**
 * Helper: Mapea roles legacy del sistema a roles RBAC
 * Soporta 'unit_owner' -> 'owner'
 *
 * EP-07 / SA-00: 'super_admin' pasa sin mapping (es nativo del RBAC nuevo).
 * Si en el futuro se introduce un alias legacy se agrega acá.
 */
export function mapLegacyRole(role: string): Role {
  const legacyMapping: Record<string, Role> = {
    'unit_owner': 'owner',
  };

  if (legacyMapping[role]) {
    return legacyMapping[role];
  }

  return isValidRole(role) ? role : 'renter';
}

/**
 * Minimum surface that `getEffectivePermissions` reads from a session user.
 *
 * Defined locally (instead of importing the `Express.User` augmentation) so
 * this helper can be consumed from `shared/` — the server middleware, the
 * auth controller, and any future FE/BE shared guard call the same function.
 */
export interface PermissionUserLike {
  role: string;
  /** EP-07 / PM-58: only relevant for property_managers; SA inherits `users.manage`
   *  via `DEFAULT_ROLE_PERMISSIONS['super_admin']` and the flag is a no-op for them. */
  userManagement?: boolean;
}

/**
 * Derives the effective permission array for a session user.
 *
 * SSOT — both the `/api/auth/user` payload (PM-58) and the new
 * `requirePermission` middleware call this. Front and back must agree on
 * what a role can do, so the derivation lives in `shared/` and there is
 * no second copy.
 *
 * Rules (mirrored from `auth.controller.ts` PM-58 / EP-07 SA-00):
 *   1. `mapLegacyRole` first — DB rows store `unit_owner`, the RBAC enum
 *      uses `owner`. A direct lookup with the raw role would resolve to
 *      `DEFAULT_ROLE_PERMISSIONS['unit_owner']` which is undefined and the
 *      renter fallback would apply.
 *   2. SA: `DEFAULT_ROLE_PERMISSIONS['super_admin']` is the final set; the
 *      `userManagement` flag is irrelevant (SA already has `users.manage`).
 *   3. PM: when `userManagement` is true, the `users` resource is upgraded
 *      to `['read', 'create', 'manage']` so PM-50's canManage('users')
 *      returns true (CA-02/CA-03/CA-05 effect at the FE).
 *   4. Any other role returns its default set unchanged.
 */
export function getEffectivePermissions(user: PermissionUserLike): Permission[] {
  const role = mapLegacyRole(user.role);
  const isSuperAdmin = user.role === 'super_admin';
  const basePermissions = DEFAULT_ROLE_PERMISSIONS[role] ?? [];
  if (isSuperAdmin || user.userManagement !== true) {
    return basePermissions;
  }
  return basePermissions.map((p) =>
    p.resource === 'users'
      ? { resource: 'users', actions: ['read', 'create', 'manage'] }
      : p,
  );
}
